import {
  chatQueueColumnConfig,
  chatAssignedColumnConfig,
  chatBotColumnConfig,
  chatClosedColumnConfig,
  emailQueueColumnConfig,
  emailAssignedColumnConfig,
  emailClosedColumnConfig,
  messagingClosedColumnConfig,
  messagingAssignedColumnConfig,
  messagingQueueColumnConfig,
  messagingBotColumnConfig
} from './column-configs';
import {
  chatAssignedHiddenColumns,
  chatBotHiddenColumns,
  chatClosedHiddenColumns,
  chatQueueHiddenColumns,
  emailAssignedHiddenColumns,
  emailClosedHiddenColumns,
  emailQueuedHiddenColumns,
  messagingAssignedHiddenColumns,
  messagingBotHiddenColumns,
  messagingClosedHiddenColumns,
  messagingQueueHiddenColumns
} from './hidden-columns/hidden-columns';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import {
  chatAssignedColumnsWithTimeCondition,
  chatBotColumnsWithTimeCondition,
  chatClosedColumnsWithTimeCondition,
  chatQueueColumnsWithTimeCondition,
  emailAssignedColumnsWithTimeCondition,
  emailClosedColumnsWithTimeCondition,
  emailQueueColumnsWithTimeCondition,
  messagingAssignedColumnsWithTimeCondition,
  messagingBotColumnsWithTimeCondition,
  messagingClosedColumnsWithTimeCondition,
  messagingQueueColumnsWithTimeCondition
} from './time-conditions/time-conditions';
import { QueueViewColumn } from './column.interface';

export const queueViewTabConfig = {
  LIVE_WORKLOAD: {
    name: 'queueView.liveWorkloadTabName',
    id: 'LIVE_WORKLOAD',
    hasDepartmentSelector: true
  },
  CHATBOT: {
    name: 'queueView.chatBotTabName',
    id: 'CHATBOT',
    hasDepartmentSelector: false,
    availableWorkItemTypes: [WorkItemTypes.CHAT, WorkItemTypes.SMS],
    CHAT: {
      name: 'chatbot-chat',
      columnConfig: chatBotColumnConfig,
      hasFolderSelector: false,
      hasActions: true,
      dynamicQueryName: 'getBotChats',
      columnSettings: [],
      defaultHiddenColumns: new Set<QueueViewColumn>(chatBotHiddenColumns),
      defaultColumnsWithTimeConditions: chatBotColumnsWithTimeCondition
    },
    SMS: {
      name: 'bot-messaging',
      columnConfig: messagingBotColumnConfig,
      hasFolderSelector: false,
      hasActions: true,
      dynamicQueryName: 'getBotTextChats',
      columnSettings: [],
      defaultHiddenColumns: new Set<QueueViewColumn>(messagingBotHiddenColumns),
      defaultColumnsWithTimeConditions: messagingBotColumnsWithTimeCondition
    }
  },
  QUEUE: {
    name: 'queueView.queueTabName',
    id: 'QUEUE',
    hasDepartmentSelector: true,
    availableWorkItemTypes: [WorkItemTypes.CHAT, WorkItemTypes.EMAIL, WorkItemTypes.SMS],
    CHAT: {
      name: 'queue-chat',
      columnConfig: chatQueueColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'unassignedChatsByFolderIDByDepartmentIDs',
      columnSettings: [],
      defaultHiddenColumns: new Set<QueueViewColumn>(chatQueueHiddenColumns),
      defaultColumnsWithTimeConditions: chatQueueColumnsWithTimeCondition,
      isFlaggingAvailable: true
    },
    EMAIL: {
      name: 'queue-email',
      columnConfig: emailQueueColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'unassignedEmailsByFolderIDByDepartmentIDs',
      defaultHiddenColumns: new Set<QueueViewColumn>(emailQueuedHiddenColumns),
      defaultColumnsWithTimeConditions: emailQueueColumnsWithTimeCondition
    },
    SMS: {
      name: 'queue-messaging',
      columnConfig: messagingQueueColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'unassignedTextMessagesByFolderIDByDepartmentIDs',
      defaultHiddenColumns: new Set<QueueViewColumn>(messagingQueueHiddenColumns),
      defaultColumnsWithTimeConditions: messagingQueueColumnsWithTimeCondition
    }
  },
  ASSIGNED: {
    name: 'queueView.assignedTabName',
    id: 'ASSIGNED',
    hasDepartmentSelector: true,
    availableWorkItemTypes: [WorkItemTypes.CHAT, WorkItemTypes.EMAIL, WorkItemTypes.SMS],
    CHAT: {
      name: 'assigned-chat',
      columnConfig: chatAssignedColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'assignedChatsByFolderIDByDepartmentIDs',
      columnSettings: [],
      defaultHiddenColumns: new Set<QueueViewColumn>(chatAssignedHiddenColumns),
      defaultColumnsWithTimeConditions: chatAssignedColumnsWithTimeCondition,
      isFlaggingAvailable: true
    },
    EMAIL: {
      name: 'assigned-email',
      columnConfig: emailAssignedColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'assignedEmailsByFolderIDByDepartmentIDs',
      defaultHiddenColumns: new Set<QueueViewColumn>(emailAssignedHiddenColumns),
      defaultColumnsWithTimeConditions: emailAssignedColumnsWithTimeCondition
    },
    SMS: {
      name: 'assigned-messaging',
      columnConfig: messagingAssignedColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      dynamicQueryName: 'assignedTextMessagesByFolderIDByDepartmentIDs',
      defaultHiddenColumns: new Set<QueueViewColumn>(messagingAssignedHiddenColumns),
      defaultColumnsWithTimeConditions: messagingAssignedColumnsWithTimeCondition
    }
  },
  CLOSED: {
    name: 'queueView.closedTabName',
    id: 'CLOSED',
    hasDepartmentSelector: true,
    availableWorkItemTypes: [WorkItemTypes.CHAT, WorkItemTypes.EMAIL, WorkItemTypes.SMS],
    CHAT: {
      name: 'closed-chat',
      columnConfig: chatClosedColumnConfig,
      hasFolderSelector: true,
      hasActions: false,
      restApiEndpointName: 'getInactiveChats',
      columnSettings: [],
      defaultHiddenColumns: new Set<QueueViewColumn>(chatClosedHiddenColumns),
      defaultColumnsWithTimeConditions: chatClosedColumnsWithTimeCondition,
      isFlaggingAvailable: true
    },
    EMAIL: {
      name: 'closed-email',
      columnConfig: emailClosedColumnConfig,
      hasFolderSelector: true,
      hasActions: true,
      restApiEndpointName: 'getInactiveEmails',
      defaultHiddenColumns: new Set<QueueViewColumn>(emailClosedHiddenColumns),
      defaultColumnsWithTimeConditions: emailClosedColumnsWithTimeCondition
    },
    SMS: {
      name: 'closed-messaging',
      columnConfig: messagingClosedColumnConfig,
      hasFolderSelector: true,
      hasActions: false,
      restApiEndpointName: 'getInactiveTextChats',
      defaultHiddenColumns: new Set<QueueViewColumn>(messagingClosedHiddenColumns),
      defaultColumnsWithTimeConditions: messagingClosedColumnsWithTimeCondition
    }
  }
};
